import { z } from 'zod';

// `endpoint` is the AP client (`/api/v2/{client}/`). The bank-feed service sits
// behind its own prefix (`/api/bankfeed/v1/`), so its calls go through
// `bankFeedEndpoint`. The user permissions grid (`bank-feed/company/permissions`)
// is NOT part of that service — it stays on AP.
import { endpoint } from '@/services/api/endpoint';
import { bankFeedApi as bankFeedClient } from '@/services/api/instances';

const bankFeedEndpoint = bankFeedClient.endpoint;

import {
  BankFeedConnectionAccountSchema,
  BankFeedConnectionSchema,
  BankFeedExportPathSchema,
  BankFeedExportStatusResponseSchema,
  BankFeedSummarySchema,
  BankFeedSyncSettingsSchema,
  BankFeedTransactionsExportBodySchema,
  BankFeedTransactionsExportResponseSchema,
  BankProvidersResponseSchema,
  ConnectionCreateBodySchema,
  ConnectionCreateResponseSchema,
  LegacyBankingConnectionCreateBodySchema,
  LegacyBankingConnectionStateSchema,
  LegacyBankingOtpBodySchema,
  LegacyBankingReauthBodySchema,
  LegacyBankingAssignAccountsBodySchema,
  LegacyBankingAssignAccountsResponseSchema,
  LegacyBankingDiscoveredAccountsResponseSchema,
  LegacyBankingLoginAccountsResponseSchema,
  LegacyBankingLinkableCompaniesResponseSchema,
  ConnectionDelegationBodySchema,
  ConnectionDelegationResponseSchema,
  ConnectionIdPathSchema,
  ConnectionsByIdsBodySchema,
  ConnectionsByIdsResponseSchema,
  ConnectionsGridResponseSchema,
  ConnectionsLimitResponseSchema,
  ConnectionUpdateBodySchema,
  ErpAccountsMatchBodySchema,
  ErpAccountsResponseSchema,
  SupportTicketBodySchema,
  TransactionsAutocompleteBodySchema,
  TransactionsAutocompleteResponseSchema,
  TransactionsGridHeadersQuerySchema,
  TransactionsGridHeadersResponseSchema,
  TransactionsGridSearchBodySchema,
  BankFeedTransactionsGridResponseSchema,
  BankFeedSettingsUserPermissionsGridResponseSchema,
  BankFeedSettingsUserPermissionsUpdateBodySchema,
  BankFeedSettingsUserPermissionsUpdateResponseSchema,
} from '@/domain/bankFeed/schemas';

export const bankFeedApi = {
  // ── reads ───────────────────────────────────────────────────────────────
  getConnections: bankFeedEndpoint({
    method: 'GET',
    url: 'connections/grid',
    response: ConnectionsGridResponseSchema,
  }),

  getConnectionsLimit: bankFeedEndpoint({
    method: 'GET',
    url: 'connections/setup',
    response: ConnectionsLimitResponseSchema,
  }),

  getProviders: bankFeedEndpoint({
    method: 'GET',
    url: 'providers',
    response: BankProvidersResponseSchema,
  }),

  getErpAccounts: bankFeedEndpoint({
    method: 'GET',
    url: 'erp-accounts',
    response: ErpAccountsResponseSchema,
  }),

  getSettingsUserPermissions: endpoint({
    method: 'GET',
    url: 'bank-feed/company/permissions',
    response: BankFeedSettingsUserPermissionsGridResponseSchema,
  }),

  getSyncSchedules: bankFeedEndpoint({
    method: 'GET',
    url: 'preferences',
    response: BankFeedSyncSettingsSchema,
  }),

  getConnectionsByIds: bankFeedEndpoint({
    method: 'POST',
    url: 'connections/grid/search',
    body: ConnectionsByIdsBodySchema,
    response: ConnectionsByIdsResponseSchema,
  }),

  getTransactionsAutocomplete: bankFeedEndpoint({
    method: 'POST',
    url: 'transactions/grid/autocomplete',
    body: TransactionsAutocompleteBodySchema,
    response: TransactionsAutocompleteResponseSchema,
  }),

  getTransactionsGridHeaders: bankFeedEndpoint({
    method: 'GET',
    url: 'transactions/grid/headers',
    query: TransactionsGridHeadersQuerySchema,
    response: TransactionsGridHeadersResponseSchema,
  }),

  getTransactionsGridSummary: bankFeedEndpoint({
    method: 'GET',
    url: 'transactions/grid/summary',
    response: BankFeedSummarySchema,
  }),

  getTransactionsGridSearch: bankFeedEndpoint({
    method: 'POST',
    url: 'transactions/grid/search',
    body: TransactionsGridSearchBodySchema,
    response: BankFeedTransactionsGridResponseSchema,
  }),

  getExport: bankFeedEndpoint({
    method: 'GET',
    url: (p: { exportId: string }) => `exports/${p.exportId}`,
    path: BankFeedExportPathSchema,
    response: BankFeedExportStatusResponseSchema,
  }),

  // ── writes ──────────────────────────────────────────────────────────────
  createConnection: bankFeedEndpoint({
    method: 'POST',
    url: 'connections',
    body: ConnectionCreateBodySchema,
    response: ConnectionCreateResponseSchema,
  }),

  // Direct bank login. Three calls because the bank may interrupt with a code:
  // create starts the sign-in, get polls it, submitOtp answers the challenge.
  createLegacyBankingConnection: bankFeedEndpoint({
    method: 'POST',
    url: 'connections/legacy-banking',
    body: LegacyBankingConnectionCreateBodySchema,
    response: LegacyBankingConnectionStateSchema,
  }),

  getLegacyBankingConnection: bankFeedEndpoint({
    method: 'GET',
    url: (p: { id: string }) => `connections/legacy-banking/${p.id}`,
    path: ConnectionIdPathSchema,
    response: LegacyBankingConnectionStateSchema,
  }),

  // Signing in again on a connection that already exists, so its id -- and every
  // ERP mapping keyed on it -- survives. Credentials are sent only when the bank
  // refused the saved ones; they then replace them for every company on that login.
  reauthLegacyBankingConnection: bankFeedEndpoint({
    method: 'POST',
    url: (p: { id: string }) => `connections/legacy-banking/${p.id}/reauth`,
    path: ConnectionIdPathSchema,
    body: LegacyBankingReauthBodySchema,
    response: LegacyBankingConnectionStateSchema,
  }),

  // The accounts that sign-in found, and who they may be given to. Two reads
  // because the second is the same list the assignment is checked against, so the
  // dropdown cannot offer a company the server would refuse.
  getLegacyBankingDiscoveredAccounts: bankFeedEndpoint({
    method: 'GET',
    url: (p: { id: string }) => `connections/legacy-banking/${p.id}/accounts`,
    path: ConnectionIdPathSchema,
    response: LegacyBankingDiscoveredAccountsResponseSchema,
  }),

  getLegacyBankingLoginAccounts: bankFeedEndpoint({
    method: 'GET',
    url: (p: { id: string }) => `connections/legacy-banking/${p.id}/login-accounts`,
    path: ConnectionIdPathSchema,
    response: LegacyBankingLoginAccountsResponseSchema,
  }),

  getLegacyBankingLinkableCompanies: bankFeedEndpoint({
    method: 'GET',
    url: 'connections/legacy-banking/linkable-companies',
    response: LegacyBankingLinkableCompaniesResponseSchema,
  }),

  assignLegacyBankingAccounts: bankFeedEndpoint({
    method: 'POST',
    url: (p: { id: string }) => `connections/legacy-banking/${p.id}/accounts`,
    path: ConnectionIdPathSchema,
    body: LegacyBankingAssignAccountsBodySchema,
    response: LegacyBankingAssignAccountsResponseSchema,
  }),

  submitLegacyBankingOtp: bankFeedEndpoint({
    method: 'POST',
    url: (p: { id: string }) => `connections/legacy-banking/${p.id}/otp`,
    path: ConnectionIdPathSchema,
    body: LegacyBankingOtpBodySchema,
    response: LegacyBankingConnectionStateSchema,
  }),

  // DPM-670: one write creates the connection and sends the authorization
  // request to the signatory (backend-confirmed contract). Also reused by
  // "Resend authorization link".
  delegateConnection: bankFeedEndpoint({
    method: 'POST',
    url: 'connections/delegate',
    body: ConnectionDelegationBodySchema,
    response: ConnectionDelegationResponseSchema,
  }),

  updateConnection: bankFeedEndpoint({
    method: 'PATCH',
    url: (p: { id: string }) => `connections/${p.id}`,
    path: ConnectionIdPathSchema,
    body: ConnectionUpdateBodySchema,
    response: BankFeedConnectionSchema,
  }),

  deleteConnection: bankFeedEndpoint({
    method: 'DELETE',
    url: (p: { id: string }) => `connections/${p.id}`,
    path: ConnectionIdPathSchema,
    response: z.unknown(),
  }),

  // AP endpoint (not the bank-feed service): opens a Freshdesk ticket.
  createSupportTicket: endpoint({
    method: 'POST',
    url: 'createTicket',
    body: SupportTicketBodySchema,
    response: z.unknown(),
  }),

  syncTransactions: bankFeedEndpoint({
    method: 'POST',
    url: (p: { id: string }) => `connections/${p.id}/transactions/sync`,
    path: ConnectionIdPathSchema,
    response: BankFeedConnectionSchema,
  }),

  matchErpAccount: bankFeedEndpoint({
    method: 'POST',
    url: 'erp-accounts/match',
    body: ErpAccountsMatchBodySchema,
    response: BankFeedConnectionAccountSchema,
  }),

  updateSettingsUserPermissions: endpoint({
    method: 'POST',
    url: 'bank-feed/company/permissions',
    body: BankFeedSettingsUserPermissionsUpdateBodySchema,
    response: BankFeedSettingsUserPermissionsUpdateResponseSchema,
  }),

  updateSyncSchedules: bankFeedEndpoint({
    method: 'POST',
    url: 'preferences',
    body: BankFeedSyncSettingsSchema,
    response: BankFeedSyncSettingsSchema,
  }),

  triggerTransactionsExport: bankFeedEndpoint({
    method: 'POST',
    url: 'transactions/grid/export',
    body: BankFeedTransactionsExportBodySchema,
    response: BankFeedTransactionsExportResponseSchema,
  }),
};
